import { useRef, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { cn } from '@design-system';

import { GripVertical } from 'lucide-react';

export const COLUMN_DND_TYPE = 'POLICY_COLUMN';

export interface ColumnDragItem {
  fundingSourceId: string;
  index: number;
  title: string;
  img?: string;
  imgClassName?: string;
}

interface DraggableColumnHeaderProps {
  fundingSourceId: string;
  index: number;
  title: string;
  img?: string;
  imgClassName?: string;
  draggedId: string | null;
  onHover: (dragId: string, hoverId: string) => void;
  onDrop: () => void;
  onDragCancel: () => void;
}

export function DraggableColumnHeader({
  fundingSourceId,
  index,
  title,
  img,
  imgClassName,
  draggedId,
  onHover,
  onDrop,
  onDragCancel,
}: DraggableColumnHeaderProps) {
  const ref = useRef<HTMLTableCellElement>(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type: COLUMN_DND_TYPE,
    item: { fundingSourceId, index, title, img, imgClassName },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    end: (_item, monitor) => {
      if (monitor.didDrop()) {
        onDrop();
      } else {
        onDragCancel();
      }
    },
  });

  const [, drop] = useDrop({
    accept: COLUMN_DND_TYPE,
    hover: (item: ColumnDragItem, monitor) => {
      if (item.fundingSourceId === fundingSourceId) return;
      if (!ref.current) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverWidth = hoverBoundingRect.right - hoverBoundingRect.left;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const hoverClientX = clientOffset.x - hoverBoundingRect.left;
      const dragIndex = item.index;
      const hoverIndex = index;

      const threshold = hoverWidth * 0.2;
      if (dragIndex < hoverIndex && hoverClientX < threshold) return;
      if (dragIndex > hoverIndex && hoverClientX > hoverWidth - threshold) return;

      onHover(item.fundingSourceId, fundingSourceId);
      item.index = hoverIndex;
    },
    drop: () => {
      onDrop();
    },
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  drag(drop(ref));

  const isBeingDragged = isDragging || draggedId === fundingSourceId;

  return (
    <th
      ref={ref}
      className={cn(
        'border-primary/15 z-9 bg-background md:w-85 md:max-w-85 sticky top-0 h-full w-64 max-w-64 overflow-hidden rounded-t-xl border p-0 align-top md:rounded-t-2xl',
        'cursor-grab active:cursor-grabbing',
        isBeingDragged && 'opacity-40',
        !isBeingDragged && draggedId !== null && 'transition-transform duration-200',
      )}
    >
      <div className="flex h-24 flex-col md:h-28">
        <div className="bg-primary/10 text-primary-dark flex items-center justify-center gap-1 py-1 text-center text-xs font-medium md:py-2">
          <GripVertical className="size-3 opacity-40" />
          {title}
        </div>
        <div className="grid flex-1 place-items-center px-3 py-3 md:px-16 md:py-4">
          {img ? (
            <img
              src={img}
              alt={title}
              className={cn('mx-auto h-auto w-28 md:w-32', imgClassName)}
            />
          ) : (
            <div className="text-primary-dark text-balance py-1.5 text-lg font-semibold md:text-xl">
              {title}
            </div>
          )}
        </div>
      </div>
    </th>
  );
}
